/**
 * useBreakdownPerformance Hook
 * Fetches analysis mode and factor performance data for the breakdown view.
 */

import { API_BASE_URL } from '@config/api';
import { useAuth } from '@contexts/AuthContext';
import type { FactorPerformance, ModePerformance } from '@parlay-picker/shared/breakdownTypes';
import { useCallback, useEffect, useState } from 'react';

export interface UseBreakdownPerformanceResult {
  modePerformance: ModePerformance[];
  factorPerformance: FactorPerformance[];
  loading: boolean;
  error: string | null;
}

/** Client-side stale-time: skip fetch if data was loaded within this window */
const STALE_TIME_MS = 5 * 60 * 1000; // 5 minutes

/** Timestamps of last successful fetch (module-level so they persist across mounts) */
let lastModeFetchedAt = 0;
let lastFactorFetchedAt = 0;

/** Cached data between mounts */
let cachedModePerformance: ModePerformance[] = [];
let cachedFactorPerformance: FactorPerformance[] = [];

export function useBreakdownPerformance(): UseBreakdownPerformanceResult {
  const { isAuthenticated, authFetch } = useAuth();
  const [modePerformance, setModePerformance] = useState<ModePerformance[]>(cachedModePerformance);
  const [factorPerformance, setFactorPerformance] =
    useState<FactorPerformance[]>(cachedFactorPerformance);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    const now = Date.now();
    const modeStale = now - lastModeFetchedAt >= STALE_TIME_MS;
    const factorStale = now - lastFactorFetchedAt >= STALE_TIME_MS;

    if (!modeStale && !factorStale) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const fetches: Promise<void>[] = [];

      if (modeStale) {
        fetches.push(
          authFetch(`${API_BASE_URL}/api/analytics/config-performance`).then(async (res) => {
            if (!res.ok) throw new Error('Failed to fetch config performance');
            const data = await res.json();
            const modes = data.analysisModePerformance || [];
            cachedModePerformance = modes;
            setModePerformance(modes);
            lastModeFetchedAt = Date.now();
          }),
        );
      }

      if (factorStale) {
        fetches.push(
          authFetch(`${API_BASE_URL}/api/analytics/factor-performance`).then(async (res) => {
            if (!res.ok) throw new Error('Failed to fetch factor performance');
            const data = await res.json();
            const factors = data.factorPerformance || [];
            cachedFactorPerformance = factors;
            setFactorPerformance(factors);
            lastFactorFetchedAt = Date.now();
          }),
        );
      }

      await Promise.all(fetches);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load breakdown performance');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, authFetch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { modePerformance, factorPerformance, loading, error };
}
